import React, { useMemo, useState } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell, LabelList,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import {
  TrendingDown, AlertTriangle, CheckCircle,
  Layers, ArrowRight, Repeat, Rocket, Receipt,
  X, Sparkles, Award, AlertCircle, ListFilter,
} from "lucide-react";

// ──────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────
const median = (arr) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const getFmtShort = (v) => {
  if (v == null) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${(v / 1e7).toFixed(1)} Cr`;
  if (abs >= 1e5) return `₹${(v / 1e5).toFixed(1)} L`;
  if (abs >= 1e3) return `₹${(v / 1e3).toFixed(0)} K`;
  return `₹${Math.round(v)}`;
};

// ──────────────────────────────────────────────────────────
// 1. PORTFOLIO / DIVERSIFICATION HEALTH GAUGE
// ──────────────────────────────────────────────────────────
export const PortfolioHealthGauge = ({ score, title = "Portfolio Quality Score", size = "md" }) => {
  const r = size === "sm" ? 65 : 82;
  const cx = size === "sm" ? 90 : 112;
  const cy = size === "sm" ? 85 : 105;
  const arc = Math.PI * r;
  const pct = Math.max(0, Math.min(100, score || 0));
  const filled = (pct / 100) * arc;
  const color = pct >= 75 ? "#10B981" : pct >= 55 ? "#F59E0B" : pct >= 35 ? "#F97316" : "#EF4444";
  const lbl = pct >= 75 ? "STRONG" : pct >= 55 ? "AVERAGE" : pct >= 35 ? "WEAK" : "CRITICAL";
  const pathD = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const w = size === "sm" ? 180 : 224;
  const h = size === "sm" ? 100 : 120;

  return (
    <div className="flex flex-col items-center">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: "visible" }}>
        {/* Background track */}
        <path d={pathD} fill="none" stroke="#1E293B" strokeWidth="13" strokeLinecap="round" />
        {/* Progress arc */}
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="13"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${arc}`}
          style={{ filter: `drop-shadow(0 0 7px ${color}aa)` }}
        />
        {/* Score number */}
        <text
          x={cx} y={cy - (size === "sm" ? 14 : 18)}
          textAnchor="middle" fill="white"
          fontSize={size === "sm" ? "28" : "34"}
          fontWeight="800"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {score == null ? "—" : Math.round(pct)}
        </text>
        {/* Zone label */}
        <text x={cx} y={cy - (size === "sm" ? 2 : 3)} textAnchor="middle" fill={color}
          fontSize="9" fontWeight="700" letterSpacing="1.8">
          {score == null ? "NO DATA" : lbl}
        </text>
        {/* /100 */}
        <text x={cx} y={cy + (size === "sm" ? 10 : 12)} textAnchor="middle" fill="#475569" fontSize="8">
          out of 100
        </text>
        {/* Tick marks */}
        {[0, 25, 50, 75, 100].map(tick => {
          const angle = Math.PI - (tick / 100) * Math.PI;
          const x1 = cx + r * Math.cos(angle);
          const y1 = cy - r * Math.sin(angle);
          const x2 = cx + (r + 9) * Math.cos(angle);
          const y2 = cy - (r + 9) * Math.sin(angle);
          return <line key={tick} x1={Math.round(x1)} y1={Math.round(y1)} x2={Math.round(x2)} y2={Math.round(y2)} stroke="#2D3748" strokeWidth="1.5" />;
        })}
        <text x={cx - r - 12} y={cy + 4} fill="#334155" fontSize="7" textAnchor="middle">0</text>
        <text x={cx + r + 12} y={cy + 4} fill="#334155" fontSize="7" textAnchor="middle">100</text>
      </svg>
      <p className="text-[10px] text-slate-500 dark:text-zinc-500 -mt-0.5 text-center">{title}</p>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// 2. RISK vs RETURN BUBBLE CHART
// ──────────────────────────────────────────────────────────
const RiskReturnTooltip = ({ active, payload, fmt }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  const isPos = d.pct_return >= 0;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 shadow-xl text-xs max-w-[220px]">
      <p className="font-semibold text-white mb-2 leading-snug">{d.fullName}</p>
      <p className={`font-bold ${isPos ? "text-emerald-400" : "text-red-400"}`}>
        Return: {isPos ? "+" : ""}{d.pct_return?.toFixed(1)}%
      </p>
      <p className="text-slate-300 mt-0.5">Weight: {d.weight?.toFixed(1)}%</p>
      <p className="text-slate-400 mt-0.5">Invested: {fmt ? fmt(d.invested) : getFmtShort(d.invested)}</p>
      <p className="text-slate-500 mt-0.5 text-[10px]">{d.asset_type}</p>
    </div>
  );
};

export const RiskReturnBubble = ({ perfCards, fmt }) => {
  const { strong, moderate, weak, negative } = useMemo(() => {
    const toPoint = c => ({
      x: Math.max(-120, Math.min(320, c.pct_return || 0)),
      y: Math.max(0, c.weight || 0),
      // sqrt(invested) keeps bubble area roughly proportional to ₹ invested
      // without letting one large holding dwarf everything else (linear) or
      // squashing all bubbles to the same size (log).
      z: Math.sqrt(Math.max(c.invested || 0, 0)),
      pct_return: c.pct_return,
      weight: c.weight,
      invested: c.invested,
      asset_type: c.asset_type,
      fullName: c.name,
    });
    const cards = (perfCards || []).filter(c => c.invested > 0);
    return {
      strong:   cards.filter(c => c.pct_return >= 15).map(toPoint),
      moderate: cards.filter(c => c.pct_return >= 0 && c.pct_return < 15).map(toPoint),
      weak:     cards.filter(c => c.pct_return < 0 && c.pct_return >= -15).map(toPoint),
      negative: cards.filter(c => c.pct_return < -15).map(toPoint),
    };
  }, [perfCards]);

  const all = [...strong, ...moderate, ...weak, ...negative];
  if (!all.length) return <p className="text-sm text-slate-400 text-center py-8">No holdings data</p>;

  const TooltipWrapper = (props) => <RiskReturnTooltip {...props} fmt={fmt} />;

  return (
    <div>
      <p className="text-[10px] text-slate-400 dark:text-zinc-500 mb-3">
        Bubble size ≈ invested amount. X = total return %. Y = portfolio weight %.
      </p>
      <ResponsiveContainer width="100%" height={240}>
        <ScatterChart margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
          <XAxis
            type="number" dataKey="x" name="Return %"
            domain={["auto", "auto"]}
            tick={{ fill: "#64748B", fontSize: 9 }}
            tickFormatter={v => `${v}%`}
            label={{ value: "Return %", position: "insideBottom", offset: -10, fill: "#64748B", fontSize: 10 }}
          />
          <YAxis
            type="number" dataKey="y" name="Weight %"
            tick={{ fill: "#64748B", fontSize: 9 }}
            tickFormatter={v => `${v}%`}
            label={{ value: "Weight %", angle: -90, position: "insideLeft", offset: 14, fill: "#64748B", fontSize: 10 }}
          />
          <ZAxis type="number" dataKey="z" range={[30, 900]} />
          <ReferenceLine x={0} stroke="#475569" strokeDasharray="4 3" />
          <Tooltip content={<TooltipWrapper />} />
          <Scatter name="+15%+" data={strong} fill="#047857" fillOpacity={0.85} />
          <Scatter name="0–15%" data={moderate} fill="#86EFAC" fillOpacity={0.75} />
          <Scatter name="-15 to 0" data={weak} fill="#F59E0B" fillOpacity={0.75} />
          <Scatter name="<-15%" data={negative} fill="#EF4444" fillOpacity={0.8} />
        </ScatterChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-3 mt-1 text-[9px] text-slate-400">
        {[
          { color: "#047857", label: "+15%+" },
          { color: "#86EFAC", label: "0–15%" },
          { color: "#F59E0B", label: "−15 to 0%" },
          { color: "#EF4444", label: "< −15%" },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// 3. ACTION MATRIX — Priority board (now in AI Overview tab)
//    Phases 1-3: Consolidate / Tax Watch / Review / Exit / Increase / Core.
//    PR C additions: top-5 banner across all buckets, click-to-drilldown,
//    doubled vertical density, resizable container.
// ──────────────────────────────────────────────────────────
const BUCKET_CONFIG = {
  consolidate: { label: "Consolidate", sub: "Overlapping funds — merge into best performer",     icon: Repeat,        color: "#A78BFA", border: "border-violet-500/25 bg-violet-500/5",   hoverBg: "hover:bg-violet-500/10"  },
  tax:         { label: "Tax Watch",   sub: "Exit candidates with material STCG cost or close to LTCG", icon: Receipt, color: "#FB923C", border: "border-orange-500/25 bg-orange-500/5",  hoverBg: "hover:bg-orange-500/10"  },
  review:      { label: "Review",      sub: "Heavy weight, weak return — evaluate alternatives", icon: AlertTriangle, color: "#F59E0B", border: "border-amber-500/25 bg-amber-500/5",    hoverBg: "hover:bg-amber-500/10"   },
  exit:        { label: "Exit",        sub: "Low weight + weak return — trim clutter",           icon: TrendingDown,  color: "#EF4444", border: "border-red-500/25 bg-red-500/5",        hoverBg: "hover:bg-red-500/10"     },
  increase:    { label: "Increase",    sub: "Strong return, low weight — consider scaling up",   icon: Rocket,        color: "#38BDF8", border: "border-sky-500/25 bg-sky-500/5",        hoverBg: "hover:bg-sky-500/10"     },
  core:        { label: "Core / Add",  sub: "Strong return, significant weight — keep / add",    icon: CheckCircle,   color: "#10B981", border: "border-emerald-500/25 bg-emerald-500/5", hoverBg: "hover:bg-emerald-500/10" },
};

// Priority weight for ranking across buckets. Drives the Top-5 banner.
// Higher = surfaced first when impact scores tie.
const BUCKET_PRIORITY = {
  consolidate: 5,
  tax:         4,
  review:      3,
  exit:        2,
  increase:    1,
  core:        0,
};

// Derive a short tax-status label + colour for an individual card row.
// Returns null when the tax payload is missing or the card is in a neutral
// state (already LTCG-eligible, or no buy_date). Never invents data.
const taxBadge = (card) => {
  const t = card?.tax;
  if (!t) return null;
  if (t.is_loss) return { text: "Loss", cls: "text-sky-400 bg-sky-500/10 border-sky-500/20" };
  if (t.tier === "LIKELY_STCG" && t.days_to_ltcg > 0 && t.days_to_ltcg <= 60) {
    return { text: `LTCG ${t.days_to_ltcg}d`, cls: "text-amber-400 bg-amber-500/10 border-amber-500/20" };
  }
  if (t.tier === "LIKELY_STCG" && (card.pct_return || 0) > 5) {
    return { text: "STCG", cls: "text-red-400 bg-red-500/10 border-red-500/20" };
  }
  return null;
};

// Impact score: how much money sits in this row × how far it strays from peers.
// Used to surface the highest-leverage rows inside each bucket.
const impactScore = (card, medR) => {
  const w = Math.max(0, card.weight || 0);
  const gap = Math.abs((card.pct_return || 0) - medR);
  return w * (gap + 1); // +1 keeps tie-broken by weight when at-median
};

const truncateName = (s, n = 26) => (s && s.length > n ? s.slice(0, n) + "…" : (s || ""));

export const ActionMatrix = ({ perfCards, overlapMatrix, fundPerformance, onSelectHolding }) => {
  const [activeTab, setActiveTab] = useState(null);
  const handleSelect = (card) => {
    if (typeof onSelectHolding === "function" && card) onSelectHolding(card);
  };
  // Map fund name → server-computed alternative payload. Populated only when
  // fundPerformance has been fetched (lazy in InsightsView). Null-safe.
  const altByName = useMemo(() => {
    const m = new Map();
    (fundPerformance?.fund_ratings || []).forEach(r => {
      if (r.alternative) m.set(r.name, r.alternative);
    });
    return m;
  }, [fundPerformance]);

  const totalUplift = fundPerformance?.total_uplift_per_year_rs || 0;

  const { buckets, medR, medW, totalValue } = useMemo(() => {
    const cards = (perfCards || []).filter(c => c.invested > 0);
    if (!cards.length) return { buckets: {}, medR: 0, medW: 0, totalValue: 0 };
    const medR = median(cards.map(c => c.pct_return || 0));
    const medW = median(cards.map(c => c.weight || 0));
    const totalValue = cards.reduce((s, c) => s + (c.current_value || 0), 0);

    // Index funds by name so overlap pairs can map back to underlying invested ₹.
    const byName = new Map();
    cards.forEach(c => byName.set(c.name, c));

    // Consolidate from real pairwise overlap (>= 40% is "high overlap" threshold
    // used elsewhere in this codebase — see services/copilot_tools/portfolio.py).
    const highPairs = (overlapMatrix || [])
      .filter(p => (p.overlap_pct || 0) >= 40)
      .slice(0, 12);
    const consolidateFundNames = new Set();
    let consolidateValue = 0;
    highPairs.forEach(p => {
      [p.fund_a, p.fund_b, p.fund1, p.fund2].forEach(n => {
        if (!n || consolidateFundNames.has(n)) return;
        consolidateFundNames.add(n);
        const c = byName.get(n);
        if (c) consolidateValue += (c.current_value || 0);
      });
    });

    const review = [], exit_ = [], increase = [], core = [];
    cards.forEach(c => {
      const r = c.pct_return || 0;
      const w = c.weight || 0;
      const highReturn = r >= medR;
      const highWeight = w >= medW;
      if (highReturn && highWeight) core.push(c);
      else if (highReturn && !highWeight) increase.push(c);
      else if (!highReturn && highWeight) review.push(c);
      else exit_.push(c);
    });

    const sortByImpact = (arr) => [...arr].sort((a, b) => impactScore(b, medR) - impactScore(a, medR));

    const bucketValue = (arr) => arr.reduce((s, c) => s + (c.current_value || 0), 0);

    // Tax Watch — overlay across Exit/Review surfacing high-tax-leverage rows.
    // Inclusion rule: holdings we'd otherwise tell the user to trim (in Exit
    // or Review) where exiting *now* triggers real STCG, OR they are close
    // enough to LTCG that waiting is materially cheaper.
    // Sort: "approaching LTCG" first (smallest days_to_ltcg), then by ₹ gain.
    const taxCandidates = [...exit_, ...review].filter(c => {
      const t = c.tax;
      if (!t || t.is_loss) return false;
      if (t.tier !== "LIKELY_STCG") return false;
      const gain = (c.current_value || 0) - (c.invested || 0);
      const approachingLtcg = t.days_to_ltcg > 0 && t.days_to_ltcg <= 60;
      const materialGain = gain > 5000 || (c.pct_return || 0) > 10;
      return approachingLtcg || materialGain;
    });
    const taxSorted = taxCandidates.sort((a, b) => {
      const da = a.tax?.days_to_ltcg ?? 99999;
      const db = b.tax?.days_to_ltcg ?? 99999;
      const aApproaching = da > 0 && da <= 60;
      const bApproaching = db > 0 && db <= 60;
      if (aApproaching !== bApproaching) return aApproaching ? -1 : 1;
      if (aApproaching && bApproaching) return da - db;
      // Otherwise rank by absolute ₹ gain
      const ag = (a.current_value || 0) - (a.invested || 0);
      const bg = (b.current_value || 0) - (b.invested || 0);
      return bg - ag;
    });

    return {
      buckets: {
        consolidate: {
          pairs: highPairs,
          fund_count: consolidateFundNames.size,
          value: consolidateValue,
        },
        tax:      { funds: taxSorted,              value: bucketValue(taxSorted) },
        review:   { funds: sortByImpact(review),   value: bucketValue(review) },
        exit:     { funds: sortByImpact(exit_),    value: bucketValue(exit_) },
        increase: { funds: sortByImpact(increase), value: bucketValue(increase) },
        core:     { funds: sortByImpact(core),     value: bucketValue(core) },
      },
      medR, medW, totalValue,
    };
  }, [perfCards, overlapMatrix]);

  const cards = (perfCards || []).filter(c => c.invested > 0);
  if (!cards.length) return <p className="text-sm text-slate-400 text-center py-8">No holdings data</p>;

  const pctOfPortfolio = (v) => totalValue > 0 ? ` · ${((v / totalValue) * 100).toFixed(0)}%` : "";

  // Quick one-line "why" string used in Top-5 cards. Computed client-side from
  // existing per-card signals — PR D extends this with fundamentals / technicals.
  const reasonFor = (card, bucketKey) => {
    const ret = card.pct_return ?? 0;
    const wt  = card.weight ?? 0;
    const alt = altByName.get(card.name);
    if (bucketKey === "review")
      return alt
        ? `${wt.toFixed(1)}% weight · −${(medR - ret).toFixed(0)}pp vs peers — alt available`
        : `${wt.toFixed(1)}% weight · −${(medR - ret).toFixed(0)}pp vs peers`;
    if (bucketKey === "exit")
      return ret < 0
        ? `Down ${ret.toFixed(0)}% · only ${wt.toFixed(1)}% of portfolio — clutter`
        : `Lagging peers · only ${wt.toFixed(1)}% of portfolio`;
    if (bucketKey === "increase")
      return `+${ret.toFixed(0)}% return · only ${wt.toFixed(1)}% weight — scale up`;
    if (bucketKey === "core")
      return `Strong return at ${wt.toFixed(1)}% weight — keep / add`;
    return "";
  };

  // Build the Top-5 list across all buckets ranked by (bucket priority, impact).
  // Excludes Core (action = "keep", not interesting for action surfacing).
  const top5 = (() => {
    const flat = [];
    ["review", "exit", "increase", "tax"].forEach(key => {
      const b = buckets[key] || { funds: [] };
      (b.funds || []).forEach(f => flat.push({
        card: f, bucketKey: key,
        score: impactScore(f, medR) * (1 + BUCKET_PRIORITY[key] * 0.15),
      }));
    });
    flat.sort((a, b) => b.score - a.score);
    return flat.slice(0, 5);
  })();

  // Doubled vertical density — rows go from ~22px to ~44px; bucket cards from
  // ~150px to ~320px. Tighter density (the prior small-card variant) was needed
  // when the matrix lived in P&B's split column. In AI Overview we have full width.
  const renderFundBucket = (key) => {
    const cfg = BUCKET_CONFIG[key];
    const b = buckets[key] || { funds: [], value: 0 };
    const Icon = cfg.icon;
    const withBadges = key === "exit" || key === "review";
    const withAlts = key === "exit" || key === "review";
    return (
      <div key={key} className={`rounded-2xl p-5 border ${cfg.border}`}>
        <div className="flex items-center gap-2.5 mb-2">
          <Icon className="w-5 h-5" style={{ color: cfg.color }} />
          <span className="text-base font-bold text-slate-900 dark:text-white">{cfg.label}</span>
          <span className="ml-auto text-xs font-bold text-slate-400">{b.funds.length}</span>
        </div>
        <p className="text-sm font-bold mb-1.5" style={{ color: cfg.color, fontFamily: "'JetBrains Mono', monospace" }}>
          {getFmtShort(b.value)}{pctOfPortfolio(b.value)}
        </p>
        <p className="text-xs text-slate-400 mb-3">{cfg.sub}</p>
        <div className="space-y-2 max-h-[260px] overflow-y-auto">
          {b.funds.slice(0, 5).map((f, i) => {
            const badge = withBadges ? taxBadge(f) : null;
            const alt = withAlts ? altByName.get(f.name) : null;
            return (
              <button
                type="button"
                key={i}
                onClick={() => handleSelect(f)}
                className={`w-full text-left rounded-lg p-2 transition-colors ${cfg.hoverBg} focus:outline-none focus:ring-1 focus:ring-white/20`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-slate-700 dark:text-zinc-200 truncate flex-1">{truncateName(f.name, badge ? 22 : 32)}</p>
                  {badge && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badge.cls} flex-shrink-0`}>{badge.text}</span>
                  )}
                  <span className={`text-sm font-bold flex-shrink-0 ${f.pct_return >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                    {f.pct_return >= 0 ? "+" : ""}{f.pct_return?.toFixed(0)}%
                  </span>
                </div>
                {alt && (
                  <div className="flex items-center gap-1.5 pl-3 mt-1">
                    <ArrowRight className="w-3 h-3 text-emerald-500/80 flex-shrink-0" />
                    <p className="text-xs text-slate-500 dark:text-zinc-500 truncate flex-1" title={`${alt.name} · ${alt.confidence} confidence`}>
                      {truncateName(alt.name, 26)}
                    </p>
                    <span className="text-xs font-bold text-emerald-500 flex-shrink-0">+{getFmtShort(alt.uplift_per_year_rs)}/yr</span>
                  </div>
                )}
              </button>
            );
          })}
          {b.funds.length > 5 && (
            <p className="text-xs text-slate-500 text-center pt-1">+{b.funds.length - 5} more</p>
          )}
          {b.funds.length === 0 && (
            <p className="text-xs text-slate-500 text-center italic py-4">None</p>
          )}
        </div>
      </div>
    );
  };

  const renderTaxRow = () => {
    const cfg = BUCKET_CONFIG.tax;
    const b = buckets.tax || { funds: [], value: 0 };
    if (!b.funds.length) return null;
    const Icon = cfg.icon;
    const approachingCount = b.funds.filter(f => {
      const d = f.tax?.days_to_ltcg ?? 0;
      return d > 0 && d <= 60;
    }).length;
    return (
      <div className={`rounded-2xl p-5 border ${cfg.border} mb-4`}>
        <div className="flex items-center gap-2.5 mb-2">
          <Icon className="w-5 h-5" style={{ color: cfg.color }} />
          <span className="text-base font-bold text-slate-900 dark:text-white">{cfg.label}</span>
          <span className="ml-auto text-xs font-bold text-slate-400">
            {b.funds.length} fund{b.funds.length === 1 ? "" : "s"}
            {approachingCount > 0 && ` · ${approachingCount} ≤ 60d to LTCG`}
          </span>
        </div>
        <p className="text-sm font-bold mb-1.5" style={{ color: cfg.color, fontFamily: "'JetBrains Mono', monospace" }}>
          {getFmtShort(b.value)}{pctOfPortfolio(b.value)} · stagger exits or wait for LTCG
        </p>
        <p className="text-xs text-slate-400 mb-3">{cfg.sub}</p>
        <div className="space-y-2 max-h-[180px] overflow-y-auto">
          {b.funds.slice(0, 4).map((f, i) => {
            const badge = taxBadge(f);
            return (
              <button
                type="button"
                key={i}
                onClick={() => handleSelect(f)}
                className={`w-full text-left rounded-lg p-2 transition-colors ${cfg.hoverBg} focus:outline-none focus:ring-1 focus:ring-white/20 flex items-center justify-between gap-2`}
              >
                <p className="text-sm text-slate-700 dark:text-zinc-200 truncate flex-1">{truncateName(f.name, 28)}</p>
                {badge && (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${badge.cls} flex-shrink-0`}>{badge.text}</span>
                )}
                <span className={`text-sm font-bold flex-shrink-0 ${f.pct_return >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                  {f.pct_return >= 0 ? "+" : ""}{f.pct_return?.toFixed(0)}%
                </span>
              </button>
            );
          })}
          {b.funds.length > 4 && (
            <p className="text-xs text-slate-500 text-center pt-1">+{b.funds.length - 4} more</p>
          )}
        </div>
      </div>
    );
  };

  const con = buckets.consolidate || { pairs: [], fund_count: 0, value: 0 };
  const showConsolidate = con.pairs && con.pairs.length > 0;
  const conCfg = BUCKET_CONFIG.consolidate;
  const ConIcon = conCfg.icon;

  // Consolidate has its own pair-based body; show all pairs (scrollable) here
  // since the tab is dedicated to this bucket now.
  const renderConsolidateBody = () => {
    if (!showConsolidate) return null;
    return (
      <div className={`rounded-2xl p-5 border ${conCfg.border}`}>
        <div className="flex items-center gap-2.5 mb-2">
          <ConIcon className="w-5 h-5" style={{ color: conCfg.color }} />
          <span className="text-base font-bold text-slate-900 dark:text-white">{conCfg.label}</span>
          <span className="ml-auto text-xs font-bold text-slate-400">
            {con.pairs.length} pair{con.pairs.length === 1 ? "" : "s"} · {con.fund_count} fund{con.fund_count === 1 ? "" : "s"}
          </span>
        </div>
        <p className="text-sm font-bold mb-1.5" style={{ color: conCfg.color, fontFamily: "'JetBrains Mono', monospace" }}>
          {getFmtShort(con.value)}{pctOfPortfolio(con.value)} in overlapping funds
        </p>
        <p className="text-xs text-slate-400 mb-3">{conCfg.sub}</p>
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {con.pairs.map((p, i) => {
            const a = p.fund_a || p.fund1 || "Fund A";
            const b = p.fund_b || p.fund2 || "Fund B";
            const pct = p.overlap_pct ?? 0;
            return (
              <div key={i} className="flex items-center justify-between gap-2 rounded-lg p-2">
                <p className="text-sm text-slate-700 dark:text-zinc-200 truncate flex-1">
                  {truncateName(a, 26)} ↔ {truncateName(b, 26)}
                </p>
                <span className="text-sm font-bold flex-shrink-0 text-violet-400">
                  {pct.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Build the ordered list of available tabs. A tab is shown only when its
  // bucket has content — that way users never click into an empty pane.
  const tabs = [];
  if (showConsolidate) {
    tabs.push({ key: "consolidate", count: con.pairs.length, value: con.value });
  }
  if ((buckets.tax?.funds?.length || 0) > 0) {
    tabs.push({ key: "tax", count: buckets.tax.funds.length, value: buckets.tax.value });
  }
  ["review", "exit", "increase", "core"].forEach(k => {
    const b = buckets[k];
    if (b && (b.funds?.length || 0) > 0) {
      tabs.push({ key: k, count: b.funds.length, value: b.value });
    }
  });

  // Pick a sane default when activeTab is null or points at a tab that no
  // longer exists (e.g. portfolio changed since last render).
  const currentTab = (activeTab && tabs.find(t => t.key === activeTab))
    ? activeTab
    : (tabs[0]?.key ?? null);

  const renderTabBody = (key) => {
    if (key === "consolidate") return renderConsolidateBody();
    if (key === "tax") return renderTaxRow();
    return renderFundBucket(key);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400 dark:text-zinc-500">
        Ranked by potential impact. Median return {medR?.toFixed(1)}% · median weight {medW?.toFixed(1)}%
        {showConsolidate && ` · ${con.pairs.length} high-overlap pair${con.pairs.length === 1 ? "" : "s"}`}
        {" · "}<span className="text-slate-500">click any holding for details</span>
      </p>

      {/* Aggregate uplift banner — sum of in-portfolio switch opportunities */}
      {totalUplift > 0 && (
        <div className="rounded-2xl p-4 border border-emerald-500/25 bg-emerald-500/5 flex items-center gap-4">
          <Rocket className="w-6 h-6 text-emerald-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900 dark:text-white">Potential uplift</p>
            <p className="text-xs text-slate-400">
              If underperformers were re-allocated to better funds you already own (1Y category-rank basis)
            </p>
          </div>
          <p className="text-xl font-bold text-emerald-400 flex-shrink-0" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            +{getFmtShort(totalUplift)}/yr
          </p>
        </div>
      )}

      {/* Top 5 Recommendations — ranked across all action buckets (excludes Core) */}
      {top5.length > 0 && (
        <div className="rounded-2xl p-5 border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900/40">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-5 h-5 text-sky-400" />
            <h4 className="text-base font-bold text-slate-900 dark:text-white">Top 5 Recommendations</h4>
            <span className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider">click to drill down</span>
          </div>
          <div className="space-y-2">
            {top5.map(({ card, bucketKey }, i) => {
              const cfg = BUCKET_CONFIG[bucketKey];
              const Icon = cfg.icon;
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => handleSelect(card)}
                  className={`w-full text-left rounded-xl p-3 border ${cfg.border} ${cfg.hoverBg} flex items-center gap-3 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20`}
                >
                  <span className="text-xs font-mono text-slate-500 w-5 flex-shrink-0">#{i + 1}</span>
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: cfg.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider flex-shrink-0" style={{ color: cfg.color }}>{cfg.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">{card.name}</p>
                    <p className="text-xs text-slate-500 dark:text-zinc-500 truncate">{reasonFor(card, bucketKey)}</p>
                  </div>
                  <span className={`text-sm font-bold flex-shrink-0 ${(card.pct_return ?? 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                    {(card.pct_return ?? 0) >= 0 ? "+" : ""}{card.pct_return?.toFixed(0)}%
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabbed per-category view: one bucket at a time, full width */}
      {tabs.length > 0 && (
        <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-zinc-900/40 p-2">
          <div
            role="tablist"
            aria-label="Action categories"
            className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-white/10 pb-2 mb-3"
          >
            {tabs.map(t => {
              const cfg = BUCKET_CONFIG[t.key];
              const Icon = cfg.icon;
              const isActive = t.key === currentTab;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 ${
                    isActive
                      ? `${cfg.border} border`
                      : "border border-transparent text-slate-500 dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-white/5"
                  }`}
                  style={isActive ? { color: cfg.color } : undefined}
                >
                  <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  <span>{cfg.label}</span>
                  <span className={`text-xs font-bold ${isActive ? "" : "text-slate-400 dark:text-zinc-500"}`}>
                    {t.count}
                  </span>
                  <span className="hidden sm:inline text-[11px] font-medium text-slate-400 dark:text-zinc-500">
                    · {getFmtShort(t.value)}
                  </span>
                </button>
              );
            })}
          </div>
          <div role="tabpanel">
            {currentTab && renderTabBody(currentTab)}
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// 4. CONTRIBUTION WATERFALL (Top Gainers + Losers)
// ──────────────────────────────────────────────────────────
const WaterfallTooltip = ({ active, payload, fmt }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 shadow-xl text-xs">
      <p className="font-semibold text-white mb-1">{d.fullName}</p>
      <p className={`font-bold ${d.value >= 0 ? "text-emerald-400" : "text-red-400"}`}>
        {d.value >= 0 ? "+" : ""}{fmt ? fmt(Math.abs(d.value)) : getFmtShort(Math.abs(d.value))}
      </p>
      <p className="text-slate-400 mt-0.5">{d.pct_return?.toFixed(1)}% total return</p>
    </div>
  );
};

export const ContributionWaterfall = ({ perfCards, fmt }) => {
  const chartData = useMemo(() => {
    const cards = (perfCards || []).filter(c => c.invested > 0 && c.abs_return != null);
    const gainers = [...cards].sort((a, b) => b.abs_return - a.abs_return).slice(0, 6);
    const losers  = [...cards].filter(c => c.abs_return < 0).sort((a, b) => a.abs_return - b.abs_return).slice(0, 4);
    return [...gainers, ...losers].map(c => ({
      name: c.name.length > 20 ? c.name.slice(0, 20) + "…" : c.name,
      fullName: c.name,
      value: c.abs_return,
      pct_return: c.pct_return,
    })).sort((a, b) => b.value - a.value);
  }, [perfCards]);

  if (!chartData.length) return <p className="text-sm text-slate-400 text-center py-8">No return data</p>;

  const TooltipWrapper = (props) => <WaterfallTooltip {...props} fmt={fmt} />;

  return (
    <div>
      <p className="text-[10px] text-slate-400 dark:text-zinc-500 mb-3">
        Absolute gain/loss contribution per holding. Top 6 winners · Top 4 losers.
      </p>
      <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 26)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 60, bottom: 0, left: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: "#64748B", fontSize: 9 }}
            tickFormatter={v => getFmtShort(v)}
          />
          <YAxis
            type="category" dataKey="name" width={120}
            tick={{ fill: "#94A3B8", fontSize: 9 }}
          />
          <ReferenceLine x={0} stroke="#475569" />
          <Tooltip content={<TooltipWrapper />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={d.value >= 0 ? "#10B981" : "#EF4444"} fillOpacity={0.85} />
            ))}
            <LabelList
              dataKey="value"
              position="right"
              formatter={v => (v >= 0 ? "+" : "") + getFmtShort(v)}
              style={{ fill: "#94A3B8", fontSize: 9 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// 5. PERFORMANCE ANALYTICS COCKPIT
//    (Hero section for Performance & Benchmark tab)
// ──────────────────────────────────────────────────────────
export const PerformanceAnalyticsCockpit = ({ perfCards, fundPerformance, portfolioHealth, fmt }) => {
  const cards = (perfCards || []).filter(c => c.invested > 0);
  const dist = fundPerformance?.performance_distribution || {};
  const score = portfolioHealth?.health_score ?? null;

  const positiveCount  = cards.filter(c => c.pct_return >= 0).length;
  const outperforming  = dist.overperforming || 0;
  const topQ           = Math.round(outperforming * 0.7); // approximation if v3 not loaded
  const toReview       = (dist.underperforming || 0) + Math.max(0, cards.filter(c => c.pct_return < -5).length - (dist.underperforming || 0));
  const totalReturn    = cards.reduce((s, c) => s + (c.abs_return || 0), 0);

  const chips = [
    { label: "Positive Returns", value: `${positiveCount} / ${cards.length}`, color: "emerald" },
    { label: "Outperforming Benchmark", value: outperforming > 0 ? `${outperforming} funds` : "—", color: "sky" },
    { label: "Need Review", value: toReview > 0 ? `${toReview} funds` : "0", color: toReview > 3 ? "amber" : "slate" },
    { label: "Total Gain", value: (totalReturn >= 0 ? "+" : "") + (fmt ? fmt(Math.abs(totalReturn)) : getFmtShort(Math.abs(totalReturn))), color: totalReturn >= 0 ? "emerald" : "red" },
  ];

  const chipStyle = {
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20",
    sky:     "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-500/20",
    amber:   "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20",
    red:     "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20",
    slate:   "bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-zinc-700",
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      {/* Hero Row: Gauge + Chips */}
      <Card className="bg-white dark:bg-[#111] border-slate-100 dark:border-white/5 rounded-2xl">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Gauge */}
            <div className="flex-shrink-0">
              <PortfolioHealthGauge score={score} title="Portfolio Health Score" />
            </div>
            {/* Divider */}
            <div className="hidden md:block w-px h-28 bg-slate-100 dark:bg-white/5" />
            {/* Chips + CTA */}
            <div className="flex-1 w-full">
              <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Performance Snapshot
              </h3>
              <p className="text-xs text-slate-400 mb-4">
                {cards.length} holdings · {score == null ? "Run analysis for health score" : score >= 75 ? "Portfolio is performing strongly" : score >= 55 ? "Portfolio needs moderate attention" : "Several holdings need review"}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {chips.map(ch => (
                  <div key={ch.label} className={`rounded-xl border px-3 py-2.5 ${chipStyle[ch.color]}`}>
                    <p className="text-[10px] font-medium opacity-75 mb-0.5">{ch.label}</p>
                    <p className="text-sm font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{ch.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row: Risk vs Return (Action Matrix now lives in AI Overview tab) */}
      {cards.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-white dark:bg-[#111] border-slate-100 dark:border-white/5 rounded-2xl">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Risk vs Return
              </h3>
              <RiskReturnBubble perfCards={cards} fmt={fmt} />
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Contribution Waterfall */}
      {cards.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="bg-white dark:bg-[#111] border-slate-100 dark:border-white/5 rounded-2xl">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Top Contributors &amp; Detractors
              </h3>
              <ContributionWaterfall perfCards={cards} fmt={fmt} />
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

// ──────────────────────────────────────────────────────────
// 6. DIVERSIFICATION HERO
//    (Hero section for Diversification & Consolidation tab)
// ──────────────────────────────────────────────────────────
const SimplificationFunnel = ({ stages, activeId, onSelect }) => {
  if (!stages?.length) return null;
  const maxVal = stages[0]?.value || 1;
  return (
    <div className="space-y-2">
      {stages.map((s) => {
        const pct = Math.max(20, (s.value / maxVal) * 100);
        const isActive = activeId === s.id;
        return (
          <div key={s.id} className="flex items-center gap-3">
            <div className="w-28 text-right">
              <p className="text-[10px] text-slate-400 leading-tight">{s.label}</p>
            </div>
            <button
              type="button"
              onClick={() => onSelect && onSelect(isActive ? null : s.id)}
              data-testid={`funnel-stage-${s.id}`}
              className="flex-1 relative h-7 flex items-center transition-transform hover:scale-[1.02]"
              style={{ paddingLeft: `${(100 - pct) / 2}%`, paddingRight: `${(100 - pct) / 2}%`, cursor: "pointer" }}
            >
              <div
                className="h-full rounded-lg flex items-center justify-center w-full transition-all"
                style={{
                  backgroundColor: s.color + (isActive ? "55" : "30"),
                  border: `1px solid ${s.color}${isActive ? "" : "50"}`,
                  boxShadow: isActive ? `0 0 0 2px ${s.color}40` : "none",
                }}
              >
                <span className="text-[10px] font-bold" style={{ color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>
                  {s.value}
                </span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ── Drill-down panel for hero chips / funnel stages ───────────────
const DrillDownPanel = ({ title, subtitle, items, emptyText, icon: Icon, tone = "slate", onClose, renderItem }) => {
  const toneCls = {
    slate:   "border-slate-200 dark:border-white/10",
    sky:     "border-sky-200 dark:border-sky-800",
    emerald: "border-emerald-200 dark:border-emerald-800",
    amber:   "border-amber-200 dark:border-amber-800",
    rose:    "border-rose-200 dark:border-rose-800",
    indigo:  "border-indigo-200 dark:border-indigo-800",
  }[tone];
  const iconCls = {
    slate:   "text-slate-500 dark:text-zinc-400",
    sky:     "text-sky-500",
    emerald: "text-emerald-500",
    amber:   "text-amber-500",
    rose:    "text-rose-500",
    indigo:  "text-indigo-500",
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className={`mt-4 rounded-xl border bg-slate-50 dark:bg-white/[0.02] ${toneCls}`}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200/60 dark:border-white/5">
          <div className="flex items-center gap-2 min-w-0">
            {Icon && <Icon className={`w-4 h-4 flex-shrink-0 ${iconCls}`} />}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200 truncate">{title}</p>
              {subtitle && <p className="text-[11px] text-slate-500 dark:text-zinc-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drilldown-close"
            className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-zinc-200 hover:bg-slate-200/60 dark:hover:bg-white/5"
            aria-label="Close drill-down"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-4 py-3 max-h-72 overflow-y-auto" data-testid="drilldown-list">
          {(!items || items.length === 0) ? (
            <p className="text-xs text-slate-500 dark:text-zinc-500 py-3 text-center">{emptyText || "Nothing to show here yet."}</p>
          ) : (
            <ul className="space-y-1.5">
              {items.map((it, i) => (
                <li key={`${it.key || it.name || i}`}>
                  {renderItem ? renderItem(it, i) : (
                    <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
                      <span className="text-xs text-slate-700 dark:text-zinc-300 truncate">{it.name}</span>
                      {it.value != null && (
                        <span className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          {it.value}
                        </span>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Pick the best fund per category by return_1y (fallback: simple_return_pct)
const buildIdealHoldings = (fundRatings = [], categoryDetail = []) => {
  const ratingByName = new Map();
  fundRatings.forEach((r) => { if (r?.name) ratingByName.set(r.name, r); });

  // Use category_detail to enumerate categories actually held (it includes truncated fund names)
  const out = [];
  categoryDetail.forEach((cat) => {
    const fundNames = cat?.funds || [];
    if (!fundNames.length) return;
    let best = null;
    fundNames.forEach((fn) => {
      // category_detail names may be truncated; match by startsWith both ways
      let match = ratingByName.get(fn);
      if (!match) {
        for (const [k, v] of ratingByName.entries()) {
          if (k.startsWith(fn) || fn.startsWith(k)) { match = v; break; }
        }
      }
      const score = match?.return_1y ?? match?.simple_return_pct ?? -Infinity;
      if (best == null || score > (best._score ?? -Infinity)) {
        best = { ...(match || { name: fn }), _score: score };
      }
    });
    if (best) {
      out.push({
        category: cat.category,
        name: best.name,
        return_1y: best.return_1y,
        rating: best.rating,
        fund_count: cat.fund_count,
        is_overlapping: cat.is_overlapping,
      });
    }
  });
  return out;
};

export const DiversificationHero = ({ perfCards, fundPerformance, deepAnalytics }) => {
  const mfCards     = (perfCards || []).filter(c => c.asset_type === "mutual_fund");
  const catOverlap  = fundPerformance?.category_overlap || [];
  const dist        = fundPerformance?.performance_distribution || {};
  const totalMF     = fundPerformance?.summary?.total_mf ?? mfCards.length;
  const overlapping = catOverlap.filter(c => c.is_overlapping).length;
  const uniqueCats  = catOverlap.filter(c => !c.is_overlapping).length;
  const matched     = fundPerformance?.summary?.matched || 0;
  const fundRatings = fundPerformance?.fund_ratings || [];
  // category_detail from deep-analytics has richer fund lists than category_overlap
  const categoryDetail = deepAnalytics?.duplication?.category_detail || catOverlap.map(c => ({
    category: c.category, funds: c.funds || [], fund_count: c.count, is_overlapping: c.is_overlapping,
  }));

  const [drill, setDrill] = useState(null); // chip-id | funnel-id | null

  // Diversification score: penalise for overlapping categories and excess funds
  const overlapPenalty = Math.min(40, overlapping * 3.5);
  const fundExcess     = Math.min(35, Math.max(0, totalMF - 18) * 1.2);
  const uniqueBonus    = Math.min(15, uniqueCats * 2);
  const divScore       = catOverlap.length > 0
    ? Math.round(Math.max(5, Math.min(100, 100 - overlapPenalty - fundExcess + uniqueBonus)))
    : null;

  const idealHoldings = useMemo(
    () => buildIdealHoldings(fundRatings, categoryDetail),
    [fundRatings, categoryDetail],
  );

  const chips = [
    { id: "total",   label: "Total MF Funds",         value: totalMF,           color: "slate" },
    { id: "overlap", label: "Overlapping Categories", value: overlapping,       color: overlapping > 5 ? "red" : overlapping > 2 ? "amber" : "emerald" },
    { id: "unique",  label: "Unique Categories",      value: uniqueCats,        color: "emerald" },
    { id: "ideal",   label: "Ideal Portfolio",        value: `${idealHoldings.length || 18} funds`, color: "sky" },
  ];

  const chipStyle = {
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20",
    sky:     "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-200 dark:border-sky-500/20",
    amber:   "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20",
    red:     "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20",
    slate:   "bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-400 border-slate-200 dark:border-zinc-700",
  };

  // Simplification funnel stages
  const withData = matched || mfCards.filter(c => c.nav_source === "AMFI").length;
  const topQuartile = Math.round((dist.overperforming || 0) * 0.85);
  const toReview = dist.underperforming || 0;
  const funnelStages = [
    { id: "funnel-total",    label: "Total Funds",    value: totalMF, color: "#6366F1" },
    { id: "funnel-data",     label: "Benchmark Data", value: withData || totalMF, color: "#3B82F6" },
    { id: "funnel-top",      label: "Top Performers", value: topQuartile || Math.round(totalMF * 0.2), color: "#10B981" },
    { id: "funnel-review",   label: "Need Review",    value: toReview || Math.round(totalMF * 0.1), color: "#F59E0B" },
  ].filter(s => s.value > 0);

  const handleSelect = (id) => setDrill(prev => prev === id ? null : id);

  // ── Build drill-down content ─────────────────────────────────────
  const drillContent = useMemo(() => {
    if (!drill) return null;
    const overlappingCats = (categoryDetail || []).filter(c => c.is_overlapping);
    const uniqueCatsList  = (categoryDetail || []).filter(c => !c.is_overlapping);
    const withDataRatings = fundRatings.filter(r => r.rating !== "no_data" || r.return_1y != null);
    const topPerformers   = [...fundRatings]
      .filter(r => r.rating === "overperforming")
      .sort((a, b) => (b.return_1y || -1e9) - (a.return_1y || -1e9));
    const needReview      = [...fundRatings]
      .filter(r => r.rating === "underperforming")
      .sort((a, b) => (a.return_1y || -1e9) - (b.return_1y || -1e9));

    const fmtPct = (v) => v == null ? "—" : `${v.toFixed(1)}%`;
    const ratingColor = (r) => ({
      overperforming:  "text-emerald-600 dark:text-emerald-400",
      meeting:         "text-slate-500 dark:text-zinc-400",
      underperforming: "text-rose-600 dark:text-rose-400",
      no_data:         "text-slate-400 dark:text-zinc-500",
    }[r] || "text-slate-500");

    if (drill === "total") {
      return {
        title: `All MF Funds — ${totalMF}`,
        subtitle: "Every mutual fund currently in your portfolio",
        icon: ListFilter,
        tone: "slate",
        emptyText: "No mutual funds in portfolio.",
        items: mfCards.map(c => ({ key: c.holding_id || c.name, name: c.name, weight: c.weight })),
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <span className="text-xs text-slate-700 dark:text-zinc-300 truncate">{it.name}</span>
            <span className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums flex-shrink-0" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {it.weight != null ? `${it.weight.toFixed(1)}%` : "—"}
            </span>
          </div>
        ),
      };
    }
    if (drill === "overlap") {
      return {
        title: `Overlapping Categories — ${overlappingCats.length}`,
        subtitle: "Categories with 2+ funds — consolidation candidates",
        icon: AlertTriangle,
        tone: "amber",
        emptyText: "No overlapping categories — nice work.",
        items: overlappingCats.map(c => ({ key: c.category, name: c.category, funds: c.funds, count: c.fund_count })),
        render: (it) => (
          <div className="py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">{it.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold tabular-nums">
                {it.count} funds
              </span>
            </div>
            <ul className="pl-3 border-l border-amber-200 dark:border-amber-800 space-y-0.5">
              {(it.funds || []).map((f, fi) => (
                <li key={fi} className="text-[11px] text-slate-600 dark:text-zinc-400 truncate">{f}</li>
              ))}
            </ul>
          </div>
        ),
      };
    }
    if (drill === "unique") {
      return {
        title: `Unique Categories — ${uniqueCatsList.length}`,
        subtitle: "Single-fund categories — already optimised",
        icon: CheckCircle,
        tone: "emerald",
        emptyText: "No unique categories yet.",
        items: uniqueCatsList.map(c => ({ key: c.category, name: c.category, funds: c.funds })),
        render: (it) => (
          <div className="py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate mb-0.5">{it.name}</div>
            {(it.funds && it.funds[0]) && (
              <p className="text-[11px] text-slate-500 dark:text-zinc-500 truncate pl-3 border-l border-emerald-200 dark:border-emerald-800">{it.funds[0]}</p>
            )}
          </div>
        ),
      };
    }
    if (drill === "ideal") {
      return {
        title: `Ideal Portfolio — ${idealHoldings.length} fund${idealHoldings.length === 1 ? "" : "s"}`,
        subtitle: "Best 1Y performer per category — your consolidation target",
        icon: Sparkles,
        tone: "sky",
        emptyText: "Load benchmark data to compute the ideal portfolio.",
        items: idealHoldings,
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">{it.name}</p>
              <p className="text-[10px] text-slate-500 dark:text-zinc-500 truncate">
                {it.category}
                {it.is_overlapping && it.fund_count > 1 ? ` · best of ${it.fund_count}` : ""}
              </p>
            </div>
            <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${ratingColor(it.rating)}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {fmtPct(it.return_1y)}
            </span>
          </div>
        ),
      };
    }
    if (drill === "funnel-total") {
      return {
        title: `Total Funds — ${totalMF}`,
        subtitle: "Funnel starting point",
        icon: ListFilter,
        tone: "indigo",
        emptyText: "No funds.",
        items: mfCards.map(c => ({ key: c.holding_id || c.name, name: c.name, weight: c.weight })),
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <span className="text-xs text-slate-700 dark:text-zinc-300 truncate">{it.name}</span>
            <span className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {it.weight != null ? `${it.weight.toFixed(1)}%` : "—"}
            </span>
          </div>
        ),
      };
    }
    if (drill === "funnel-data") {
      return {
        title: `With Benchmark Data — ${withDataRatings.length}`,
        subtitle: "Funds with 1Y benchmark return matched",
        icon: CheckCircle,
        tone: "sky",
        emptyText: "No benchmark matches yet.",
        items: withDataRatings,
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">{it.name}</p>
              <p className="text-[10px] text-slate-500 dark:text-zinc-500 truncate">{it.scheme_category || it.sector}</p>
            </div>
            <span className={`text-xs font-bold tabular-nums flex-shrink-0 ${ratingColor(it.rating)}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {fmtPct(it.return_1y)}
            </span>
          </div>
        ),
      };
    }
    if (drill === "funnel-top") {
      return {
        title: `Top Performers — ${topPerformers.length}`,
        subtitle: "Funds beating their category average",
        icon: Award,
        tone: "emerald",
        emptyText: "No top performers yet — try refreshing benchmark data.",
        items: topPerformers,
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">{it.name}</p>
              <p className="text-[10px] text-slate-500 dark:text-zinc-500 truncate">
                {it.scheme_category || it.sector}
                {it.alpha != null ? ` · α ${it.alpha > 0 ? "+" : ""}${it.alpha.toFixed(1)}` : ""}
              </p>
            </div>
            <span className="text-xs font-bold tabular-nums flex-shrink-0 text-emerald-600 dark:text-emerald-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {fmtPct(it.return_1y)}
            </span>
          </div>
        ),
      };
    }
    if (drill === "funnel-review") {
      return {
        title: `Need Review — ${needReview.length}`,
        subtitle: "Funds trailing category average by 2%+",
        icon: AlertCircle,
        tone: "amber",
        emptyText: "No underperformers — looking good.",
        items: needReview,
        render: (it) => (
          <div className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-white dark:hover:bg-white/[0.04]">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">{it.name}</p>
              <p className="text-[10px] text-slate-500 dark:text-zinc-500 truncate">
                {it.scheme_category || it.sector}
                {it.alpha != null ? ` · α ${it.alpha.toFixed(1)}` : ""}
              </p>
            </div>
            <span className="text-xs font-bold tabular-nums flex-shrink-0 text-rose-600 dark:text-rose-400" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {fmtPct(it.return_1y)}
            </span>
          </div>
        ),
      };
    }
    return null;
  }, [drill, categoryDetail, fundRatings, idealHoldings, mfCards, totalMF]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card className="bg-white dark:bg-[#111] border-slate-100 dark:border-white/5 rounded-2xl">
        <CardContent className="p-5 md:p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Gauge */}
            <div className="flex-shrink-0">
              <PortfolioHealthGauge score={divScore} title="Diversification Score" />
            </div>
            {/* Divider */}
            <div className="hidden md:block w-px h-28 bg-slate-100 dark:bg-white/5" />
            {/* Chips */}
            <div className="flex-1 w-full">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-4 h-4 text-purple-500" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Diversification Overview
                </h3>
              </div>
              <p className="text-xs text-slate-400 mb-4">
                {divScore == null
                  ? "Load benchmark data to compute diversification score"
                  : divScore >= 70
                  ? "Well-diversified portfolio with minimal category overlap"
                  : divScore >= 45
                  ? `${overlapping} overlapping categories — consider consolidating`
                  : `Significantly over-diversified — reducing to ${idealHoldings.length || 18} core funds recommended`}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {chips.map(ch => {
                  const isActive = drill === ch.id;
                  return (
                    <button
                      key={ch.id}
                      type="button"
                      onClick={() => handleSelect(ch.id)}
                      data-testid={`hero-chip-${ch.id}`}
                      className={`text-left rounded-xl border px-3 py-2.5 transition-all hover:shadow-sm ${chipStyle[ch.color]} ${
                        isActive ? "ring-2 ring-offset-1 ring-offset-transparent ring-current scale-[1.02]" : ""
                      }`}
                    >
                      <p className="text-[10px] font-medium opacity-75 mb-0.5">{ch.label}</p>
                      <p className="text-sm font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{ch.value}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Funnel (hidden on mobile) */}
            {funnelStages.length > 1 && (
              <>
                <div className="hidden lg:block w-px h-28 bg-slate-100 dark:bg-white/5" />
                <div className="hidden lg:block flex-shrink-0 w-52">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3 text-center">Simplification Funnel</p>
                  <SimplificationFunnel
                    stages={funnelStages}
                    activeId={drill}
                    onSelect={handleSelect}
                  />
                </div>
              </>
            )}
          </div>

          {/* Drill-down panel */}
          <AnimatePresence>
            {drill && drillContent && (
              <DrillDownPanel
                title={drillContent.title}
                subtitle={drillContent.subtitle}
                icon={drillContent.icon}
                tone={drillContent.tone}
                items={drillContent.items}
                emptyText={drillContent.emptyText}
                renderItem={drillContent.render}
                onClose={() => setDrill(null)}
              />
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
};
